<template>
    <div class="modal fade" id="swap_pose">
        <div class="modal-dialog modal-dialog-centered modal-lg">
            <div class="modal-content border-none shadow-xl">
                <div class="flex items-center justify-between bg-gray-100 py-2 px-2 rounded-t-md">
                    <h4 class="m-0">Swap Pose</h4>
                    <div class="text-right mr-2 text-gray-700 hover:text-gray-900 hover:underline cursor-pointer lg:hidden" data-dismiss="modal">
                        <font-awesome-icon icon="xmark"></font-awesome-icon>
                        Close
                    </div>
                </div>
                <div class="modal-body p-3">
                    <Alert type="warning" align="left" title="Caution" class="my-3">
                        Swapping any picture will overwrite all custom retouching you may have already selected.
                    </Alert>

                    <div class="flex items-center justify-around">
                        <div class="p-2 flex-shrink-0">
                            <h6 class="font-semibold text-center mb-2">Selected Pose</h6>
                            <figure class="relative">
                                <img
                                    v-if="changing.path"
                                    :src="changing.path || '/images/default.png'"
                                    :alt="'F' + changing.film + '-' + changing.frame"
                                    class="w-32">
                                <figcaption class="absolute bottom-0 inset-x-0 text-white text-center font-medium text-shadow">
                                    {{imageFNum(changing)}}
                                </figcaption>
                            </figure>
                        </div>
                        <font-awesome-icon :icon="['fas', 'arrow-right']" size="2x"></font-awesome-icon>
                        <div class="p-2" v-if="preview.path">
                            <h6 class="font-semibold text-center mb-2">Swap To</h6>
                            <figure class="relative">
                                <img
                                    :src="preview.path"
                                    :alt="'F' + preview.film + '-' + preview.frame"
                                    class="w-32">
                                <figcaption class="absolute bottom-0 inset-x-0 text-white text-center font-medium text-shadow">
                                    {{imageFNum(preview)}}
                                </figcaption>
                            </figure>
                        </div>

                        <div class="p-2" v-if="!preview.path">
                            <Alert type="info" align="left" title="Notice" class="my-3">
                                You cannot swap with an already selected pose.
                            </Alert>
                        </div>
                    </div>

                    <h5 class="text-center mt-6 mb-2">Available Poses</h5>
                    <div class="grid grid-flow-row grid-cols-3 gap-3 lg:grid-cols-4 lg:gap-4 mb-12">
                        <div v-for="(pic, index) in favorites" :key="index">
                            <figure class="relative w-full">
                                <font-awesome-layers class="absolute top-0 left-0 fa-2x ml-1 mt-1" v-if="pic.is_selected">
                                    <font-awesome-icon icon="certificate" class="text-green-500"></font-awesome-icon>
                                    <font-awesome-icon icon="check" transform="shrink-8" class="text-white"></font-awesome-icon>
                                </font-awesome-layers>
                                <img
                                    @click="updateSelection(pic, $event)"
                                    :src="pic.path || '/images/default.png'"
                                    :alt="'F' + pic.film + '-' + pic.frame"
                                    class="cursor-pointer w-full">
                                <figcaption class="absolute bottom-0 inset-x-0 text-white text-center font-medium text-shadow">
                                    {{imageFNum(pic)}}
                                </figcaption>
                            </figure>
                        </div>
                    </div>

                    <div class="flex items-center absolute bottom-0 right-0 mb-2 mr-2">
                        <p class="text-gray-600 m-0">Don't see what you're looking for?</p>
                        <button class="btn text-sm bg-transparent text-red-500 hover:bg-red-500 border border-red-500 hover:text-white ml-3" @click="goToFavorites">
                            <font-awesome-icon icon="heart"></font-awesome-icon>
                            Edit Favorites
                        </button>
                    </div>
                </div>
                <div class="bg-gray-100 text-right py-4 px-2 rounded-b-md">
                    <button type="button" class="btn text-gray-700 border border-gray-600 hover:bg-gray-600 hover:text-white" data-dismiss="modal">Cancel</button>
                    <button type="button" class="btn text-white bg-blue-500 border border-blue-500 hover:bg-blue-600 hover:border-blue-600 disabled:opacity-75 disabled:cursor-not-allowed" data-dismiss="modal" @click="swap" :disabled="!preview.path">Swap Selection</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { library } from '@fortawesome/fontawesome-svg-core';
    import { faXmark, faArrowRight, faCertificate, faCheck, faHeart } from '@fortawesome/free-solid-svg-icons';

    import Alert from '../../../components/app/Alert';

    export default {
        props: ['favorites', 'current', 'change'],

        data() {
            return {
                selected: this.current || [],
                changing: {},
                preview: {},
            }
        },

        components: {
            Alert,
        },

        created() {
            library.add(faXmark, faArrowRight, faCertificate, faCheck, faHeart);
        },

        mounted() {
            this.setPose();
            this.checkSelections();
        },

        methods: {
            setPose() {
                let self = this;
                this.favorites.forEach(pose => {
                    if(pose.film === self.change.film && pose.frame === self.change.frame) {
                        self.changing = {
                            film: pose.film,
                            frame: pose.frame,
                            path: pose.path
                        }
                    }
                });
            },

            checkSelections() {
                let self = this;
                self.favorites.forEach(pose => {
                    pose.is_selected = false;
                });

                this.selected.forEach(selection => {
                    let sel = self.favorites.find(pose => pose.f_num === selection.f_num);
                    sel.is_selected = true;
                });
            },

            updateSelection(pic, e) {
                let images = document.querySelectorAll('.modal .border-2.border-red-500');

                for (let i = 0; i < images.length; i++) {
                    const image = images[i];

                    image.classList.remove('border-2', 'border-red-500', 'shadow-inner');
                }

                this.preview = {};
                if(!pic.is_selected) {
                    this.preview = pic;
                    e.target.classList.add('border-2', 'border-red-500', 'shadow-inner')
                }
            },

            swap() {
                let obj = {
                    f_num: this.preview.f_num,
                    film: this.preview.film,
                    frame: this.preview.frame,
                    path: this.preview.path
                }
                this.$emit('swapPose', this.change, obj);
            },

            goToFavorites() {
                this.$emit('goToFavorites');
            },

            imageFNum(pose) {
                return parseInt(pose.film).toString().substr(-2) + '-' + parseInt(pose.frame).toString().padStart(3, '0');
            }
        }
    }
</script>
